import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ReportBusyService } from '../_services/report-busy.service';

/**
 * ENG-6006: the one place that knows a report is being fetched.
 *
 * Every page under Reports used to be responsible for saying so itself, and most of
 * them did not: the Dashboard's Search left the screen unchanged for six seconds and
 * people clicked it again. Counting the requests instead means a page cannot forget,
 * and a page added later is covered on the day it ships.
 */
@Injectable()
export class ReportBusyInterceptor implements HttpInterceptor {
  /**
   * The reporting APIs, by the part of the path that identifies them. Kept as a list
   * rather than one regex so that adding a report is a one-line change.
   */
  private static readonly REPORT_PATHS = [
    '/report/',                 // mini report and the monthly statement
    '/reports/',                // the Reports module's own pages
    '/report-builder/',         // custom reports
    '/menu_insights',           // Product Mix
    '/kpi_insights',            // Key Performance Indicators
    '/tip-report/',
    '/auto_gratuity/',
    '/order_comp/',
    '/payroll-report/',
    '/shift-checkout/',
    '/nmi-settlements/',        // Tenders and deposits
    '/ach-deposits/',           // Bank deposits
  ];

  /**
   * Fetches that run on their own schedule rather than because someone asked for a
   * report. Flashing the bar for these would make it meaningless.
   */
  private static readonly BACKGROUND = [
    '/reports/alerts/',         // the alerts bell, polled
    '/reports/live/',           // the live board refreshes itself every minute
    '/reports/settings/',       // which reports this store shows
    '/report-builder/settings/',
    '/report-builder/catalog/',
    '/reports/glossary/',       // cached for an hour
  ];

  constructor(private busy: ReportBusyService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isReportFetch(request)) {
      return next.handle(request);
    }
    this.busy.started();
    // finalize, not tap: it fires on success, on error and on an unsubscribe, which is
    // what a page does when the store changes mid-request
    return next.handle(request).pipe(finalize(() => this.busy.finished()));
  }

  private isReportFetch(request: HttpRequest<any>): boolean {
    const url = request.url;
    if (ReportBusyInterceptor.BACKGROUND.some(p => url.includes(p))) {
      return false;
    }
    return ReportBusyInterceptor.REPORT_PATHS.some(p => url.includes(p));
  }
}
